import { create } from 'zustand';
import { persist, PersistOptions } from 'zustand/middleware';
import { createDocumentActions } from './documentActions';
import { createFolderActions } from './folderActions';
import { initialWorkspaceState } from './state';
import { WorkspaceStore, WorkspacePersistData } from './types';

// 导出所有公共类型
export * from './types';

// 导出所有选择器
export * from './selectors';

// 定义持久化选项
const persistOptions: PersistOptions<
  WorkspaceStore,
  WorkspacePersistData
> = {
  name: 'workspace-storage',
  storage: {
    getItem: (name) => {
      const storedData = localStorage.getItem(name);
      return storedData ? JSON.parse(storedData) : null;
    },
    setItem: (name, value) => {
      localStorage.setItem(name, JSON.stringify(value));
    },
    removeItem: (name) => {
      localStorage.removeItem(name);
    },
  },
  
  // 只持久化文档和文件夹数据，忽略其他状态
  partialize: (state) => ({
    documents: state.documents,
    folders: state.folders
  }),
};

/**
 * 创建工作区存储
 * 整合了状态、文档操作和文件夹操作
 */
export const useWorkspaceStore = create<WorkspaceStore>()(
  persist(
    (set) => ({
      // 初始状态
      ...initialWorkspaceState,
      
      // 整合文档操作
      ...createDocumentActions(set),
      
      // 整合文件夹操作
      ...createFolderActions(set),
    }),
    persistOptions
  )
);

/**
 * 工作区操作API
 * 为外部提供一个更简洁的接口，隐藏Zustand实现细节
 */
export const workspaceAPI = {
  // 文档操作
  createDocument: (title?: string, folderId?: string) => 
    useWorkspaceStore.getState().createDocument(title, folderId),
  
  updateDocument: (id: string, content: string, title?: string) => 
    useWorkspaceStore.getState().updateDocument(id, content, title),
  
  deleteDocument: (id: string) => 
    useWorkspaceStore.getState().deleteDocument(id),
  
  openDocument: (id: string) => 
    useWorkspaceStore.getState().openDocument(id),
  
  closeDocument: (id: string) => 
    useWorkspaceStore.getState().closeDocument(id),
    
  setActiveDocument: (id: string | null) => 
    useWorkspaceStore.getState().setActiveDocument(id),
  
  // 文件夹操作
  createFolder: (name: string, parentId?: string) => 
    useWorkspaceStore.getState().createFolder(name, parentId),
  
  renameFolder: (id: string, name: string) => 
    useWorkspaceStore.getState().renameFolder(id, name),
  
  deleteFolder: (id: string) => 
    useWorkspaceStore.getState().deleteFolder(id),
  
  moveDocumentToFolder: (documentId: string, folderId: string) => 
    useWorkspaceStore.getState().moveDocumentToFolder(documentId, folderId),
  
  moveFolderToFolder: (folderId: string, parentId: string) => 
    useWorkspaceStore.getState().moveFolderToFolder(folderId, parentId),
    
  setActiveFolder: (id: string | null) => 
    useWorkspaceStore.getState().setActiveFolder(id),
}; 